import { useState } from 'react'
import { Trans, useTranslation } from 'react-i18next'

import { ValidationSubmissionDocument } from '@percent/admin-dashboard/api/types'
import { Loader } from '@percent/admin-dashboard/common/components'
import { useMutation, useValidationSubmissionsAccess } from '@percent/admin-dashboard/common/hooks'
import { useServices } from '@percent/admin-dashboard/containers/service/ServiceContext'
import { OrganisationTypes } from '@percent/domain/verifications'
import {
  AcknowledgeModal,
  Alert,
  Button,
  CardDashboard,
  ConfirmationModal,
  DocumentsGallery,
  Modal
} from '@percent/lemonade'
import styles from './AgentVerificationDocuments.module.scss'
import { AgentVerificationDocumentsProps } from './AgentVerificationDocuments.types'
import { AgentVerificationMarkDocAsFraudModal } from './agentVerificationMarkDocAsFraudModal/AgentVerificationMarkDocAsFraudModal'
import { LLMReasoning } from './llmReasoning/LLMReasoning'

export function AgentVerificationDocuments({
  agentVerificationId,
  agentVerificationLogs,
  isLoadingAgentVerificationLogs,
  refreshAgentVerificationLogs,
  refreshAgentVerificationData,
  refreshAgentVerificationDocuments,
  isLoading,
  documents,
  agentEmail,
  isOrgDataAvailable,
  isStatusPending,
  orgType,
  withCard = true
}: Readonly<AgentVerificationDocumentsProps>) {
  const [isRequestDocumentModalOpened, setIsRequestDocumentModalOpened] = useState(false)
  const [documentRequestStatus, setDocumentRequestStatus] = useState<'success' | 'error'>()
  const [isMarkAsFraudModalOpened, setIsMarkAsFraudModalOpened] = useState(false)
  const { agentVerificationService } = useServices()
  const { t } = useTranslation()
  // Marking a document as fraud is a validation_submissions action, not an agent
  // verification one — the document belongs to the submission.
  const { canMarkFraud } = useValidationSubmissionsAccess()

  const [selectedDocument, setSelectedDocument] = useState<ValidationSubmissionDocument | null>(documents?.[0] ?? null)

  const [{ isLoading: isDocumentRequestLoading }, { apiFunc: requestDocument }] = useMutation(
    agentVerificationService.requestAgentVerificationDocument,
    () => setDocumentRequestStatus('success'),
    () => setDocumentRequestStatus('error')
  )

  const isAnyDocumentMarkedAsFraud = documents?.some(d => d.markedAsFraudAt)
  const hasDocuments = Array.isArray(documents) && documents?.length > 0
  const requestDocumentationText = t('typography.requestDocumentation')
  const documentRequestSent = agentVerificationLogs?.find(
    log => log.type === 'agent_verification_document_request_email_sent'
  )
  const documentUploaded = agentVerificationLogs?.find(log => log.type === 'agent_verification_document_uploaded')
  const isFullView =
    !documentRequestSent && isOrgDataAvailable && isStatusPending && orgType !== OrganisationTypes.Education

  const toggleRequestDocumentModal = () => setIsRequestDocumentModalOpened(!isRequestDocumentModalOpened)
  const toggleMarkAsFraudModal = () => setIsMarkAsFraudModalOpened(v => !v)
  const onSubmit = () => {
    if (!agentVerificationId) return

    requestDocument({ id: agentVerificationId })
  }
  const onClose = () => {
    refreshAgentVerificationLogs()
    refreshAgentVerificationData()
    toggleRequestDocumentModal()
    setDocumentRequestStatus(undefined)
  }

  const onSelectedFileChange = (fileUrl: string) => {
    if (fileUrl !== selectedDocument?.url) {
      setSelectedDocument(documents?.find(d => d.url === fileUrl) ?? null)
    }
  }

  const getDocumentVariant = (document: ValidationSubmissionDocument) => {
    if (document.markedAsFraudAt) return 'warning'

    if (document.type === 'agent_verification') return 'green'

    return 'default'
  }

  const actionButtons = !isLoadingAgentVerificationLogs && (
    <div className={styles.buttonsWrapper}>
      {(documents?.length ?? 0) > 0 && canMarkFraud && (
        <Button variant="secondary" onClick={toggleMarkAsFraudModal}>
          {t('button.markAsFraud')}
        </Button>
      )}

      {isFullView && (
        <Button
          onClick={toggleRequestDocumentModal}
          data-testid="open-request-documentation-modal"
          disabled={!agentEmail}
        >
          {requestDocumentationText}
        </Button>
      )}
    </div>
  )

  const content = (
    <>
      {isAnyDocumentMarkedAsFraud && <Alert variant="warning" title={t('typography.avDocuments.markedAsFraud')} />}
      <LLMReasoning document={selectedDocument} />
      {(isLoading || isLoadingAgentVerificationLogs) && <Loader />}
      {!isLoading && !isLoadingAgentVerificationLogs && (
        <div className={styles.messageWrapper}>
          {!hasDocuments && <>{t('typography.noDocumentsApplication')} </>}
          {documentRequestSent && !documentUploaded && (
            <Alert variant="info" title={t('typography.documentationRequestSent', { email: agentEmail })} />
          )}
          {isFullView && !documentUploaded && (
            <Trans
              i18nKey="typography.moreDocumentsInformationNeeded"
              components={{ b: <b /> }}
              values={{
                buttonText: requestDocumentationText
              }}
            />
          )}
        </div>
      )}
      {!isLoading && hasDocuments && (
        <DocumentsGallery
          files={documents.map(d => ({
            url: d.url,
            variant: getDocumentVariant(d),
            translation: d.translation
          }))}
          withPreview
          previewUrl="/document-preview"
          translationContentTitle={t('typography.translatedFromLanguage')}
          onSelectedFileChange={onSelectedFileChange}
        />
      )}

      {isRequestDocumentModalOpened && (
        <Modal
          open={isRequestDocumentModalOpened}
          onClose={toggleRequestDocumentModal}
          data-testid="request-documentation-modal"
          aria-labelledby="request-documentation-modal"
        >
          {!documentRequestStatus && (
            <ConfirmationModal
              title={requestDocumentationText}
              loading={isDocumentRequestLoading}
              primaryButtonText={requestDocumentationText}
              primaryBtnTestId="request-documentation-button"
              secondaryButtonText={t('button.cancel')}
              handleSubmit={onSubmit}
              handleClose={toggleRequestDocumentModal}
            >
              {t('dialog.requestDocumentation.description', { agentEmail })}
            </ConfirmationModal>
          )}
          {documentRequestStatus === 'success' && (
            <AcknowledgeModal
              result="positive"
              title={t('dialog.requestDocumentation.success.title')}
              buttonText={t('button.close')}
              handleClose={onClose}
            />
          )}
          {documentRequestStatus === 'error' && (
            <AcknowledgeModal
              result="negative"
              title={t('dialog.requestDocumentation.error.title')}
              description={t('dialog.requestDocumentation.error.description')}
              buttonText={t('button.close')}
              handleClose={onClose}
            />
          )}
        </Modal>
      )}

      {isMarkAsFraudModalOpened && (
        <AgentVerificationMarkDocAsFraudModal
          isMarkAsFraudModalOpened={isMarkAsFraudModalOpened}
          toggleMarkAsFraudModal={toggleMarkAsFraudModal}
          selectedDocument={selectedDocument}
          refresh={refreshAgentVerificationDocuments}
        />
      )}
    </>
  )

  if (!withCard) {
    return (
      <div data-testid="request-documentation-documents">
        {actionButtons}
        {content}
      </div>
    )
  }

  return (
    <CardDashboard
      title={t('typography.documents')}
      action={actionButtons}
      data-testid="request-documentation-documents"
    >
      {content}
    </CardDashboard>
  )
}
